import React, { useState, useEffect, useRef } from 'react';
import { Spinner } from './Spinner';
export const SearchResults = ({ loading, isEmpty, products = [], libraryResources = [], pages = [], renderPageLink, renderProductCard, renderResourceLink, }) => {
    const resultsContainerRef = useRef(null);
    const [visibleProducts, setVisibleProducts] = useState(3);
    const [visibleResources, setVisibleResources] = useState(0);
    const [visiblePages, setVisiblePages] = useState(0);
    const productsFullyLoaded = visibleProducts >= products.length;
    const resourcesFullyLoaded = visibleResources >= libraryResources.length;
    const hasMoreProducts = products.length > visibleProducts;
    const hasMoreResources = libraryResources.length > visibleResources && (productsFullyLoaded || products.length === 0);
    const hasMorePages = pages.length > visiblePages &&
        (productsFullyLoaded || products.length === 0) &&
        (resourcesFullyLoaded || libraryResources.length === 0);
    useEffect(() => {
        const container = resultsContainerRef.current;
        if (!container)
            return;
        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container.parentElement || container;
            if (scrollTop + clientHeight >= scrollHeight - 100) {
                if (hasMoreProducts) {
                    setVisibleProducts((prev) => Math.min(prev + 5, products.length));
                }
                else if (!productsFullyLoaded) {
                    setVisibleProducts(products.length);
                }
                else if (hasMoreResources) {
                    setVisibleResources((prev) => Math.min(prev + 5, libraryResources.length));
                }
                else if (!resourcesFullyLoaded) {
                    setVisibleResources(libraryResources.length);
                }
                else if (hasMorePages) {
                    setVisiblePages((prev) => Math.min(prev + 5, pages.length));
                }
            }
        };
        const scrollContainer = container.parentElement || container;
        scrollContainer.addEventListener('scroll', handleScroll);
        return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }, [
        hasMoreProducts,
        hasMoreResources,
        hasMorePages,
        productsFullyLoaded,
        resourcesFullyLoaded,
        products.length,
        libraryResources.length,
        pages.length,
    ]);
    useEffect(() => {
        setVisibleProducts(Math.min(3, products.length));
        if (products.length === 0) {
            setVisibleResources(Math.min(5, libraryResources.length));
            setVisiblePages(Math.min(5, pages.length));
        }
        else {
            setVisibleResources(0);
            setVisiblePages(0);
        }
    }, [products, libraryResources, pages]);
    if (loading) {
        return (React.createElement("div", { className: "py-8 flex flex-col gap-3.5 items-center" },
            React.createElement(Spinner, null),
            React.createElement("p", { className: "uppercase text-gray-500 text-xs font-normal" }, "Loading Your Results")));
    }
    if (isEmpty) {
        return (React.createElement("div", { className: "py-8 text-center" },
            React.createElement("h3", { className: "text-xl font-medium mb-2" }, "No Results Found"),
            React.createElement("p", { className: "text-gray-500" }, "Try adjusting your search terms.")));
    }
    return (React.createElement("div", { ref: resultsContainerRef },
        products.length > 0 && (React.createElement("ul", { className: "grid grid-cols-1 gap-6 mb-6" }, products.slice(0, visibleProducts).map((product) => (React.createElement("li", { className: "border-b border-b-brand-rule-grey", key: product.id }, renderProductCard(product)))))),
        libraryResources.length > 0 && (productsFullyLoaded || products.length === 0) && (React.createElement("ul", { className: "grid grid-cols-1 gap-6 mb-6" }, libraryResources.slice(0, visibleResources).map((resource) => (React.createElement("li", { className: "border-b border-b-brand-rule-grey", key: resource.id }, renderResourceLink(resource)))))),
        pages.length > 0 &&
            (productsFullyLoaded || products.length === 0) &&
            (resourcesFullyLoaded || libraryResources.length === 0) && (React.createElement("ul", { className: "grid grid-cols-1 gap-6 [&_li:last-child]:border-b-0" }, pages.slice(0, visiblePages).map((page) => (React.createElement("li", { className: "border-b border-b-brand-rule-grey", key: page.id }, renderPageLink(page))))))));
};
